import { zodResolver } from '@hookform/resolvers/zod';
import {
  changePasswordSchema,
  expiredPasswordChangeSchema,
  type ChangePasswordInput,
} from '@qualicloud/contracts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  Field,
  FormError,
  IconAlertTriangle,
  IconCheck,
  IconClose,
  IconInfo,
  Input,
  PasswordInput,
  cn,
  useToast,
} from '@/design-system';
import { authApi, toUserMessage } from '@/lib/api';
import { assessPassword, type PasswordAssessment } from '@/lib/password-strength';
import { useAuth } from '@/providers/auth-provider';

/**
 * Troca de senha.
 *
 * O medidor de força reflete a POLÍTICA REAL da organização
 * (`organization.passwordPolicy`), não uma heurística genérica: comprimento
 * mínimo, classes de caractere exigidas e validade vêm do mesmo objeto que o
 * IDENTITY aplica no servidor (§11.300(b)).
 *
 * O que o cliente NÃO consegue avaliar e por isso é dito explicitamente:
 * reuso de senha anterior (`historySize`) e presença em listas de senhas
 * vazadas. Esses dois são verificados só no servidor, e a tela avisa que podem
 * recusar a senha mesmo com todos os requisitos verdes — melhor prevenir a
 * frustração do que fingir que a validação local é completa.
 */
interface ChangePasswordForm extends ChangePasswordInput {
  readonly username: string;
}

export function ChangePasswordPage() {
  const { user, organization, refreshProfile, setMustChangePassword } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const search = useSearch({ strict: false }) as unknown as {
    expired?: boolean;
    username?: string;
  };

  const policy = organization?.passwordPolicy;
  const forced = user?.mustChangePassword === true || search.expired === true;

  /**
   * DOIS CAMINHOS, e a diferença não é cosmética.
   *
   * COM sessão  → `POST /auth/password`, que identifica o ator pelo token.
   * SEM sessão  → `POST /auth/password/expired`, que reautentica com usuário +
   *               senha vencida. É o caso do primeiro acesso e o da senha que
   *               expirou: o IDENTITY recusa o login com 403 e NÃO abre sessão
   *               (§11.300 — senha vencida não dá acesso ao sistema), então não
   *               existe token para a rota autenticada usar. Sem este caminho o
   *               usuário ficava preso nesta tela.
   */
  const sessionless = !user;

  const form = useForm<ChangePasswordForm>({
    // `organizationSlug` e `totp` são opcionais no schema, então o formulário
    // de quatro campos o satisfaz. (Nada de `.omit()` aqui: os dois schemas
    // terminam em `.refine`, e `ZodEffects` não expõe `.omit`.)
    resolver: zodResolver(sessionless ? expiredPasswordChangeSchema : changePasswordSchema) as never,
    defaultValues: {
      username: search.username ?? '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = form.watch('newPassword');
  const assessment: PasswordAssessment | null = useMemo(
    () => (policy ? assessPassword(newPassword ?? '', policy) : null),
    [newPassword, policy],
  );

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (sessionless) {
        await authApi.changeExpiredPassword({
          username: values.username.trim(),
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        });
        toast({
          tone: 'success',
          title: 'Senha alterada',
          description: 'Entre novamente com a nova senha.',
        });
        // Nenhuma sessão foi aberta — de propósito. O usuário entra de novo.
        void navigate({ to: '/login' });
        return;
      }

      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      setMustChangePassword(false);
      await refreshProfile();
      toast({
        tone: 'success',
        title: 'Senha alterada',
        description:
          'Sua senha foi atualizada e todas as demais sessões foram encerradas por segurança.',
      });
      void navigate({ to: '/' });
    } catch (error) {
      form.setError('root', { message: toUserMessage(error) });
      form.setValue('currentPassword', '');
    }
  });

  return (
    <main className="mx-auto w-full max-w-xl px-4 py-10">
      <Card padding="lg">
        <h1 className="text-xl font-semibold text-content">Trocar senha</h1>

        {forced ? (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
          >
            <IconAlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              <strong className="font-semibold">Troca obrigatória.</strong> Sua senha expirou ou
              precisa ser definida no primeiro acesso. Enquanto ela não for trocada, você não pode
              usar o sistema nem assinar registros eletronicamente.
            </span>
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-4" noValidate>
          {form.formState.errors.root ? (
            <FormError title="A senha não foi alterada">
              {form.formState.errors.root.message}
            </FormError>
          ) : null}

          {sessionless ? (
            <Field label="Usuário" required error={form.formState.errors.username?.message}>
              <Input
                {...form.register('username')}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                readOnly={Boolean(search.username)}
              />
            </Field>
          ) : null}

          <Field
            label="Senha atual"
            required
            error={form.formState.errors.currentPassword?.message}
          >
            <PasswordInput
              {...form.register('currentPassword')}
              autoComplete="current-password"
              autoFocus
            />
          </Field>

          <Field label="Nova senha" required error={form.formState.errors.newPassword?.message}>
            <PasswordInput {...form.register('newPassword')} autoComplete="new-password" />
          </Field>

          {assessment ? <StrengthMeter assessment={assessment} /> : null}

          <Field
            label="Confirmar nova senha"
            required
            error={form.formState.errors.confirmPassword?.message}
          >
            <PasswordInput {...form.register('confirmPassword')} autoComplete="new-password" />
          </Field>

          {policy ? (
            <p className="flex items-start gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2.5 text-xs text-content-muted">
              <IconInfo size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                O servidor também recusa senhas que você já usou nas últimas{' '}
                <strong className="font-semibold">{policy.historySize}</strong> trocas e senhas que
                aparecem em listas públicas de vazamentos. Esses dois critérios não podem ser
                verificados nesta tela.
                {policy.expiryDays > 0 ? (
                  <>
                    {' '}
                    A nova senha valerá por{' '}
                    <strong className="font-semibold">{policy.expiryDays} dias</strong>.
                  </>
                ) : null}
              </span>
            </p>
          ) : null}

          <div className="mt-1 flex flex-wrap justify-end gap-2">
            {!forced ? (
              <Button variant="secondary" onClick={() => void navigate({ to: '/' })}>
                Cancelar
              </Button>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              loading={form.formState.isSubmitting}
              disabled={assessment ? !assessment.meetsPolicy : false}
            >
              Alterar senha
            </Button>
          </div>
        </form>
      </Card>
    </main>
  );
}

const LEVEL_BAR: Record<PasswordAssessment['level'], string> = {
  insufficient: 'bg-danger',
  weak: 'bg-warning',
  fair: 'bg-info',
  strong: 'bg-success',
};

/**
 * Medidor de força.
 *
 * ACESSIBILIDADE:
 *  · `role="progressbar"` com `aria-valuetext` textual ("Forte") — o número
 *    puro não significa nada para quem ouve;
 *  · cada requisito é um item de lista com ÍCONE de forma distinta (visto vs.
 *    X) e texto de estado oculto, nunca apenas verde/cinza;
 *  · o bloco é `aria-live="polite"`: o usuário digita e ouve o progresso sem
 *    ser interrompido a cada tecla.
 */
function StrengthMeter({ assessment }: { readonly assessment: PasswordAssessment }) {
  return (
    <div className="rounded-md border border-border bg-surface-sunken p-3" aria-live="polite">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-content-subtle">
          Força da senha
        </span>
        <span
          className={cn(
            'text-sm font-semibold',
            assessment.level === 'insufficient' && 'text-danger-content',
            assessment.level === 'weak' && 'text-warning-content',
            assessment.level === 'fair' && 'text-info-content',
            assessment.level === 'strong' && 'text-success-content',
          )}
        >
          {assessment.levelLabel}
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuenow={assessment.score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={assessment.levelLabel}
        aria-label="Força da senha"
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface"
      >
        <div
          className={cn('h-full rounded-full transition-[width]', LEVEL_BAR[assessment.level])}
          style={{ width: `${assessment.score}%` }}
        />
      </div>

      <p className="mt-2 text-xs text-content-muted">{assessment.advice}</p>

      <ul className="mt-3 flex flex-col gap-1.5">
        {assessment.requirements.map((requirement) => (
          <li key={requirement.id} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden="true"
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                requirement.satisfied
                  ? 'bg-success text-white'
                  : 'bg-neutral-subtle text-content-subtle',
              )}
            >
              {requirement.satisfied ? (
                <IconCheck size={11} strokeWidth={3} />
              ) : (
                <IconClose size={10} strokeWidth={3} />
              )}
            </span>
            <span className={requirement.satisfied ? 'text-content' : 'text-content-muted'}>
              {requirement.label}
            </span>
            <span className="qc-sr-only">
              {requirement.satisfied ? '— requisito atendido' : '— requisito pendente'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
